<template>
  <FormCard
    v-for="fieldGroup in props.fieldGroups"
    :key="fieldGroup.key"
    :data-testid="`self-service-form-googleads-display-group-${fieldGroup.key}`"
    :data-group-key="fieldGroup.key"
    :title="fieldGroup.name"
    :class="{ 'selected-card': openCard === fieldGroup.key }"
    :is-open="openCard === fieldGroup.key"
    @toggle="handleFormCardToggle(fieldGroup.key)"
    :completed="completedCards.has(fieldGroup.key)"
    @enter-pressed="handleSaveAndContinue(fieldGroup.key)">
    <template #form-content>
      <div
        class="form-section"
        v-for="field in fieldGroup.properties"
        :key="field.key"
        :data-testid="`self-service-field-${field.key}`"
        :data-field-key="field.key">
        <GoogleAdsTargeting
          v-if="field.type === 'locations' && field.key === 'target_locations' && providerId"
          v-model="targetingValue"
          :provider-id="providerId"
          :errors="errors[field.key] || null" />

        <GoogleMultipleHeadlines
          v-else-if="field.type === 'headlines'"
          v-model="obj[field.key]"
          :options="field.options"
          :errors="errors[field.key] || null" />

        <GoogleMultipleDescriptions
          v-else-if="field.type === 'descriptions'"
          v-model="obj[field.key]"
          :options="field.options"
          :errors="errors[field.key] || null" />

        <GoogleAdsLogoImage
          v-else-if="field.key === 'display_ad_logo_image' && field.type === 'image'"
          v-model="companyLogos"
          :errors="errors[field.key] || null"
          :companyId="companyId" />

        <GoogleAdsMarketingImages
          v-else-if="field.key === 'display_ad_marketing_images' && field.type === 'image.multi'"
          v-model="obj[field.key]"
          :errors="errors[field.key] || null"
          :companyId="companyId"
          :max-images="field.options?.max" />

        <LeadSelect
          v-else-if="field.type === 'select'"
          v-model="obj[field.key]"
          :options="field.options || []"
          :label="field.label"
          :errors="errors[field.key] || null" />

        <LeadInput
          v-else-if="field.type === 'text'"
          v-model="obj[field.key]"
          :label="field.label"
          :errors="errors[field.key] || null" />
      </div>
      <div
        class="form-actions float-right"
        :data-testid="`self-service-save-action-${fieldGroup.key}`"
        :data-i18n-key="isLast(fieldGroup.key) ? 'btn.save' : 'btn.save_and_continue'">
        <LeadButton variant="secondary" :loading="loadingSave" @click="handleSaveAndContinue(fieldGroup.key)">
          {{ isLast(fieldGroup.key) ? $t('btn.save') : $t('btn.save_and_continue') }}
          <template v-if="!isLast(fieldGroup.key)" #icon>
            <IconArrowBigRightFilled size="16" stroke-width="1.5" />
          </template>
        </LeadButton>
      </div>
    </template>
  </FormCard>
</template>

<script setup lang="ts">
import { watch, ref, type Ref, onMounted, computed, nextTick } from 'vue';
import FormCard from '@/components/orders/form_cards/FormCard.vue';
import LeadButton from '@/components/generic/LeadButton.vue';
import LeadSelect from '@/components/form/LeadSelect.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import { IconArrowBigRightFilled } from '@tabler/icons-vue';
import { updateCampaignObject } from '@/api/campaign';
import type { FieldGroup } from '@/types/types';
import GoogleAdsTargeting from '@/components/form/GoogleAdsTargeting.vue';
import GoogleMultipleHeadlines from '@/components/form/GoogleMultipleHeadlines.vue';
import GoogleMultipleDescriptions from '@/components/form/GoogleMultipleDescriptions.vue';
import GoogleAdsMarketingImages from '@/components/form/GoogleAdsMarketingImages.vue';
import GoogleAdsLogoImage from '@/components/form/GoogleAdsLogoImage.vue';
import { useProviderStore } from '@/stores/providers';
import { storeToRefs } from 'pinia';
import type { Order } from '@/types/types';
import type { ICompany } from '@/types/company';
import { updateCompany, updateCompanyImages } from '@/api/company';
import { request } from '@/api/client';
import translator from '@/locale/translator';

interface GoogleAdsDisplayForm {
  target_locations: Array<{
    target_location_label: string;
    target_location_id: number;
    target_country_label: string;
    target_country_code: string;
    bid_modifier: number;
  }>;
  target_proximity_locations: Array<{
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    radius: number;
    radius_unit: 'KILOMETERS' | 'MILES';
    bid_modifier: number;
  }>;
  headlines: string;
  descriptions: string;
  display_ad_marketing_images: Array<{ id: number; type: string }>;
  display_ad_logo_image: number;
  [key: string]: any;
}

const props = defineProps<{
  providerId: string | null | undefined;
  fieldGroups: FieldGroup[];
  object: any;
  order: Order;
}>();

const emit = defineEmits<{
  (e: 'update:object', obj: any): void;
}>();

const providerStore = useProviderStore();
const { completedDecks } = storeToRefs(providerStore);
const { obj } = storeToRefs(providerStore) as unknown as { obj: Ref<GoogleAdsDisplayForm> };
const openCard = ref<string | null>(null);
const companyLogos = ref<any[]>([]);
const loadingSave = ref(false);

const completedCards = computed(() => {
  if (!props.providerId) return new Set<string>();
  return completedDecks.value[props.providerId] || new Set<string>();
});

const companyId = computed(() => {
  return props.order?.company?.data?.id || 0;
});

const errors = ref<Record<string, any>>({});

const targetingValue = computed({
  get() {
    return {
      target_locations: obj.value.target_locations || [],
      target_proximity_locations: obj.value.target_proximity_locations || [],
    };
  },
  set(newValue) {
    obj.value.target_locations = newValue.target_locations;
    obj.value.target_proximity_locations = newValue.target_proximity_locations;
  },
});

onMounted(async () => {
  // Open the first card by default if field groups are available
  if (props.fieldGroups.length > 0 && !openCard.value) {
    openCard.value = props.fieldGroups[0].key;
  }
});

const handleFormCardToggle = (key: string) => {
  // If the clicked card is already open, close it
  if (openCard.value === key) {
    openCard.value = null;
  } else {
    // Otherwise, open the clicked card (and close any other open card)
    openCard.value = key;
  }
};

const handleSaveAndContinue = async (currentCard: string) => {
  loadingSave.value = true;
  // Update the object with the values of the current card
  const updateData = {};
  props.object.groups.forEach((group) => {
    if (group.key === currentCard) {
      Object.values(group.properties).forEach(async (property: any) => {
        if (property.key === 'target_locations') {
          // For location targeting, save both target_locations and target_proximity_locations
          if (obj.value.target_locations !== undefined) {
            updateData['target_locations'] = obj.value.target_locations;
          }
          if (obj.value.target_proximity_locations !== undefined) {
            updateData['target_proximity_locations'] = obj.value.target_proximity_locations;
          }
        } else if (property.key === 'display_ad_marketing_images') {
          updateData[property.key] = obj.value[property.key] || null;
          updateData['display_ad_type'] = 'RESPONSIVE_DISPLAY_AD';
        } else if (property.key === 'display_ad_logo_image') {
          updateData[property.key] = companyLogos.value.length > 0 ? companyLogos.value[0].id : null;
        } else if (obj.value[property.key] !== undefined) {
          updateData[property.key] = obj.value[property.key];
        }
      });
    }
  });

  try {
    const res = await updateCampaignObject(props.providerId ?? '', updateData);
    // if the update is successful, then update the company logo
    const logoImageId = updateData['display_ad_logo_image'];
    await updateCompany(companyId.value, {
      ...(props.order?.company?.data ?? {}),
      logo_image_id: logoImageId,
    } as ICompany);
    if (logoImageId) {
      await updateCompanyImages(companyId.value, [logoImageId]);
    } else {
      await updateCompanyImages(companyId.value, []);
    }
    emit('update:object', res.data);

    // Clear any previous errors on success
    errors.value = {};
    setTimeout(() => {
      loadingSave.value = false;
      providerStore.addCompletedCard(currentCard, props.providerId ?? '');

      // If there's a next card to open, set it as the open card
      const currentIndex = props.fieldGroups.findIndex((group) => group.key === currentCard);
      if (currentIndex >= 0 && currentIndex < props.fieldGroups.length - 1) {
        openCard.value = props.fieldGroups[currentIndex + 1].key;
      } else {
        // If no next card, close the current card
        openCard.value = null;
      }
    }, 500);
  } catch (error: any) {
    if (error.responseJson) {
      errors.value = error.responseJson.errors?.provider_values || {};
    } else {
      console.error('Unexpected error:', error);
    }
    loadingSave.value = false;
  }
};

const isLast = (key: string) => {
  const lastIndex = props.fieldGroups.length - 1;
  return props.fieldGroups[lastIndex].key === key;
};

watch(
  () => props.fieldGroups,
  (newValue) => {
    if (newValue) {
      openCard.value = null;
      if (props.fieldGroups.length > 0 && !openCard.value) {
        nextTick(() => {
          openCard.value = props.fieldGroups[0].key;
        });

        props.object.groups.forEach((group: any) => {
          if (props.object?.confirmed || props.object?.values_valid) {
            providerStore.addCompletedCard(group.key, props.providerId ?? '');
          }

          if (group.properties) {
            Object.values(group.properties).forEach(async (property: any) => {
              if (property.key === 'display_ad_marketing_images') {
                // Ensure it is an array
                obj.value[property.key] = Array.isArray(property.value)
                  ? property.value.map((v: any) => {
                      return typeof v === 'object' && v !== null
                        ? { id: v.id || v, type: v.type || 'image' }
                        : { id: v, type: 'image' };
                    })
                  : [];
              } else {
                obj.value[property.key] = property.value || '';
              }

              // display logo if campaign logo is set, otherwise use company logo
              // saving the logo will update the campaign logo field and the company logo too
              if (property.key === 'display_ad_logo_image') {
                companyLogos.value = [];
                if (property.value) {
                  const response = await request('GET', `/v1/account/media/images/${property.value}`, { meta: 1 });
                  companyLogos.value = response.data ? [response.data] : [];
                } else {
                  const logoData = props.order?.company?.data?.logo_image?.data;
                  companyLogos.value = logoData ? [logoData] : [];
                  obj.value[property.key] = logoData ? logoData.id : null;
                }
              }

              nextTick(() => {
                // Ensure at least one default location if both arrays are empty
                if (obj.value?.target_locations.length === 0 && obj.value?.target_proximity_locations.length === 0) {
                  obj.value.target_locations = [
                    {
                      target_location_label: translator.get('campaigns.please_select_location'),
                      target_location_id: 0,
                      target_country_label: '',
                      target_country_code: '',
                      bid_modifier: 1.0,
                    },
                  ];
                }
              });
            });
          }
        });
      }
    }
  },
  { immediate: true }
);

watch(
  () => companyLogos.value,
  (newValue) => {
    if (newValue.length > 0) {
      obj.value.display_ad_logo_image = newValue[0].id;
    } else {
      obj.value.display_ad_logo_image = 0;
    }
  }
);
</script>

<style scoped lang="scss">
.float-right {
  float: right;
}

.form-section {
  margin-bottom: 30px;
}
</style>
